import React, { useState } from 'react'

interface WaterThresholdsSectionProps {
  hysteresis: number
  onHysteresisChange: (value: number) => void
  disabled?: boolean
}

export function WaterThresholdsSection({ hysteresis, onHysteresisChange, disabled }: WaterThresholdsSectionProps) {
  const [validationError, setValidationError] = useState<string>('')

  // Handle value change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value)
    const validValue = isNaN(value) ? 0 : Math.max(0, value)
    onHysteresisChange(validValue)
    if (validationError) {
      setValidationError('')
    }
  }

  // Fixed values (read-only)
  const waterInTrigger = 25
  const waterOutTrigger = 15

  return (
    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
      <h3 className="text-sm font-medium text-gray-900 mb-4">Water Sensor Thresholds</h3>

      <p className="text-xs text-gray-600 mb-4">
        Configure alert thresholds for water sensors. Hysteresis prevents alert thrashing by requiring the sensor
        value to cross a threshold and move beyond it before triggering the opposite alert.
      </p>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Setting
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Value (%)
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Water IN Trigger */}
            <tr className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm font-medium text-gray-900">Water IN Trigger</td>
              <td className="px-6 py-4">
                <input
                  type="number"
                  value={waterInTrigger}
                  readOnly
                  disabled
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 opacity-50 cursor-not-allowed"
                />
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">Alert when water level rises above this threshold</td>
            </tr>

            {/* Water OUT Trigger */}
            <tr className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm font-medium text-gray-900">Water OUT Trigger</td>
              <td className="px-6 py-4">
                <input
                  type="number"
                  value={waterOutTrigger}
                  readOnly
                  disabled
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 opacity-50 cursor-not-allowed"
                />
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">Alert when water level drops below this threshold</td>
            </tr>

            {/* Hysteresis */}
            <tr className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                Hysteresis
                <span className="ml-2 text-xs text-gray-500 cursor-help" title="Hysteresis prevents alert thrashing">
                  ℹ️
                </span>
              </td>
              <td className="px-6 py-4">
                <div>
                  <input
                    type="number"
                    min="0"
                    value={hysteresis}
                    onChange={handleChange}
                    disabled={disabled}
                    className={`w-20 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                      validationError ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {validationError && <p className="text-xs text-red-600 mt-1">{validationError}</p>}
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">
                Buffer zone to prevent repeated alerts near thresholds
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Explanation section */}
      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">How Hysteresis Works</h4>
        <div className="text-xs text-blue-800 space-y-1">
          <p>
            <strong>Water IN Alert:</strong> Triggers at {waterInTrigger}%, then requires water level to drop below{' '}
            {waterInTrigger - hysteresis}% before it can trigger again.
          </p>
          <p>
            <strong>Water OUT Alert:</strong> Triggers at {waterOutTrigger}%, then requires water level to rise above{' '}
            {waterOutTrigger + hysteresis}% before it can trigger again.
          </p>
          <p className="mt-2 italic">
            This prevents alerts from rapidly toggling when the water level fluctuates near the threshold.
          </p>
        </div>
      </div>
    </div>
  )
}
